import { Mail, MessageCircle, Instagram, Bell } from "lucide-react";

/* ---------- iOS text-selection context menu (floating capsule) ---------- */
export const IOSSelectionMenu = ({ items, className = "", testid }) => {
    return (
        <div
            data-testid={testid}
            className={`relative inline-flex items-stretch bg-[#1a1a1a] rounded-[10px] shadow-[0_10px_30px_-6px_rgba(0,0,0,0.8)] border border-white/10 overflow-hidden ${className}`}
        >
            {items.map((label, i) => (
                <div
                    key={label}
                    className={`px-3.5 md:px-4 py-1.5 md:py-2 text-[13px] md:text-[14px] text-white font-medium tracking-tight ${
                        i !== items.length - 1 ? "border-r border-white/10" : ""
                    }`}
                >
                    {label}
                </div>
            ))}
            {/* tail */}
            <div className="absolute -bottom-[6px] left-8 w-3 h-3 bg-[#1a1a1a] border-r border-b border-white/10 rotate-45" />
        </div>
    );
};

/* ---------- iOS Notes-style highlight marker ---------- */
export const IOSHighlight = ({ children, variant = "red" }) => {
    const bg =
        variant === "red"
            ? "bg-[#FF2A2A] text-white"
            : "bg-[#3a3a3a] text-white";
    return (
        <span
            className={`relative inline-block px-3 py-0.5 ${bg}`}
            style={{ boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" }}
        >
            {children}
        </span>
    );
};

/* ---------- iOS blue-text selection highlight ---------- */
export const IOSTextSelection = ({ children }) => {
    return (
        <span className="bg-[#FF2A2A]/30 text-white relative">
            <span className="absolute -left-[2px] top-[4%] bottom-[4%] w-[3px] bg-[#FF2A2A]" />
            <span className="absolute -right-[2px] top-[4%] bottom-[4%] w-[3px] bg-[#FF2A2A]" />
            {children}
        </span>
    );
};

/* ---------- iOS notification banner ---------- */
export const IOSNotification = ({
    app = "YOUR AGENCY",
    title = "Nuevo brief recibido",
    message = "Tap para responder →",
    time = "ahora",
    icon: Icon = MessageCircle,
    iconBg = "#FF2A2A",
    className = "",
    testid,
}) => {
    return (
        <div
            data-testid={testid}
            className={`relative bg-[#1a1a1a]/90 backdrop-blur-2xl border border-white/10 rounded-[22px] shadow-[0_20px_60px_-10px_rgba(0,0,0,0.8)] p-3.5 flex items-start gap-3 max-w-sm ${className}`}
        >
            <div
                className="shrink-0 w-9 h-9 rounded-[9px] flex items-center justify-center"
                style={{ backgroundColor: iconBg }}
            >
                <Icon className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] tracking-[0.15em] uppercase text-white/60 truncate">
                        {app}
                    </span>
                    <span className="text-[10px] text-white/40">{time}</span>
                </div>
                <div className="text-white text-[13px] font-semibold leading-tight mt-0.5 truncate">
                    {title}
                </div>
                <div className="text-white/60 text-[12px] leading-snug mt-0.5 line-clamp-2">
                    {message}
                </div>
            </div>
        </div>
    );
};

/* ---------- iOS app icon (squircle with optional red badge) ---------- */
export const IOSAppIcon = ({
    label,
    icon: Icon,
    bg = "#FF2A2A",
    badge,
    className = "",
    testid,
}) => {
    return (
        <div className={`flex flex-col items-center gap-2 ${className}`} data-testid={testid}>
            <div className="relative">
                <div
                    className="w-14 h-14 md:w-16 md:h-16 rounded-[18px] flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(0,0,0,0.6)] border border-white/10"
                    style={{ backgroundColor: bg }}
                >
                    <Icon className="w-6 h-6 md:w-7 md:h-7 text-white" />
                </div>
                {badge ? (
                    <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-[#FF2A2A] border-2 border-[#0A0A0A] rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                        {badge}
                    </span>
                ) : null}
            </div>
            <span className="text-[11px] text-white/70 font-medium">{label}</span>
        </div>
    );
};

/* ---------- iMessage-style bubble ---------- */
export const IMessageBubble = ({
    text,
    side = "left",
    className = "",
    testid,
}) => {
    const isRight = side === "right";
    return (
        <div
            data-testid={testid}
            className={`max-w-[280px] px-3.5 py-2 rounded-[20px] text-[14px] leading-snug ${
                isRight
                    ? "bg-[#FF2A2A] text-white rounded-br-[6px] ml-auto"
                    : "bg-[#1f1f1f] text-white rounded-bl-[6px] border border-white/5"
            } ${className}`}
        >
            {text}
        </div>
    );
};

export const Icons = { Mail, MessageCircle, Instagram, Bell };
